import React from "react";
import { useTranslation } from "react-i18next";
import { Actions, Box, Button, Drawer, Stack, TextField } from "braid-design-system";
import { useToast } from "../../hooks/useToast";
import NumericTextField from "../NumericTextField";

export interface GLAccountFormData {
    acc_code: string;
    acc_name: string;
    acc_branch_id: string;
}

export type GLAccountToEdit = {
    id?: number | null;
    status?: number | boolean | null;
} & Partial<{
    [K in keyof GLAccountFormData]: GLAccountFormData[K] | null;
}>;

interface FormErrors {
    acc_code?: string;
    acc_name?: string;
    acc_branch_id?: string;
}

interface CreateUpdateGLAccountDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    glAccountToEdit?: GLAccountToEdit;
    defaultBranchId?: string | null;
    onSave: (accountData: GLAccountFormData, editingAccount?: GLAccountToEdit) => Promise<void> | void;
    onGLAccountSaved?: () => void;
    isReadOnly?: boolean;
}

const defaultFormData: GLAccountFormData = {
    acc_code: "",
    acc_name: "",
    acc_branch_id: "",
};

const CreateUpdateGLAccountDrawer: React.FC<CreateUpdateGLAccountDrawerProps> = ({
    isOpen,
    onClose,
    glAccountToEdit,
    defaultBranchId,
    onSave,
    onGLAccountSaved,
    isReadOnly = false,
}) => {
    const { t } = useTranslation();
    const toast = useToast();

    const [formData, setFormData] = React.useState<GLAccountFormData>(defaultFormData);
    const [errors, setErrors] = React.useState<FormErrors>({});
    const [submitting, setSubmitting] = React.useState(false);

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (glAccountToEdit) {
            setFormData({
                acc_code: glAccountToEdit.acc_code ?? "",
                acc_name: glAccountToEdit.acc_name ?? "",
                acc_branch_id: glAccountToEdit.acc_branch_id ?? defaultBranchId ?? "",
            });
        } else {
            setFormData({
                ...defaultFormData,
                acc_branch_id: defaultBranchId ?? "",
            });
        }

        setErrors({});
    }, [defaultBranchId, glAccountToEdit, isOpen]);

    const updateText = (key: keyof GLAccountFormData, value: string) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
        if (errors[key as keyof FormErrors]) {
            setErrors((prev) => ({ ...prev, [key]: undefined }));
        }
    };

    const validate = () => {
        const nextErrors: FormErrors = {};

        if (!formData.acc_code.trim()) {
            nextErrors.acc_code = t("page.gl_account_management.drawer.validation.required", { defaultValue: "Required" });
        }

        if (!formData.acc_name.trim()) {
            nextErrors.acc_name = t("page.gl_account_management.drawer.validation.required", { defaultValue: "Required" });
        }

        if (!formData.acc_branch_id.trim()) {
            nextErrors.acc_branch_id = t("page.gl_account_management.drawer.validation.required", { defaultValue: "Required" });
        }

        if (formData.acc_code.length > 20) {
            nextErrors.acc_code = t("page.gl_account_management.drawer.validation.max_length_code", { defaultValue: "Maximum 20 characters allowed" });
        }

        if (formData.acc_name.length > 50) {
            nextErrors.acc_name = t("page.gl_account_management.drawer.validation.max_length_name", { defaultValue: "Maximum 50 characters allowed" });
        }

        if (formData.acc_branch_id.length > 10) {
            nextErrors.acc_branch_id = t("page.gl_account_management.drawer.validation.max_length_branch", { defaultValue: "Maximum 10 characters allowed" });
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (isReadOnly) {
            return;
        }

        if (submitting) {
            return;
        }

        if (!validate()) {
            toast.showError(t("page.gl_account_management.drawer.validation.fix_errors", { defaultValue: "Please fix validation errors" }));
            return;
        }

        try {
            setSubmitting(true);
            await onSave(formData, glAccountToEdit);

            if (onGLAccountSaved) {
                onGLAccountSaved();
            }

            toast.showSuccess(
                glAccountToEdit
                    ? t("page.gl_account_management.drawer.success.updated", { defaultValue: "GL account updated successfully" })
                    : t("page.gl_account_management.drawer.success.created", { defaultValue: "GL account created successfully" }),
            );

            onClose();
        } catch (error: any) {
            toast.showError(error?.message || t("page.gl_account_management.drawer.error.save_failed", { defaultValue: "Failed to save GL account" }));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Drawer
            open={isOpen}
            onClose={onClose}
            title={
                glAccountToEdit
                    ? t("page.gl_account_management.drawer.title.update", { defaultValue: "Update GL Account" })
                    : t("page.gl_account_management.drawer.title.create", { defaultValue: "Create New GL Account" })
            }
            width="small"
        >
            <form onSubmit={handleSubmit} autoComplete="off">
                <Box padding="large">
                    <Stack space="large">
                        <TextField
                            label={t("page.gl_account_management.drawer.label.acc_code", { defaultValue: "Code*" })}
                            value={formData.acc_code}
                            tone={errors.acc_code ? "critical" : undefined}
                            message={errors.acc_code}
                            onChange={(e) => updateText("acc_code", e.currentTarget.value)}
                        />
                        <TextField
                            label={t("page.gl_account_management.drawer.label.acc_name", { defaultValue: "Name*" })}
                            value={formData.acc_name}
                            tone={errors.acc_name ? "critical" : undefined}
                            message={errors.acc_name}
                            onChange={(e) => updateText("acc_name", e.currentTarget.value)}
                        />
                        <NumericTextField
                            label={t("page.gl_account_management.drawer.label.acc_branch_id", { defaultValue: "Branch*" })}
                            value={formData.acc_branch_id}
                            mode="integer"
                            maxLength={10}
                            tone={errors.acc_branch_id ? "critical" : undefined}
                            message={errors.acc_branch_id}
                            disabled
                            onChange={(value) => updateText("acc_branch_id", value)}
                        />

                        <Box display="flex" justifyContent="flexEnd">
                            <Actions>
                                <Button type={isReadOnly ? "button" : "submit"} variant="ghost" tone="brandAccent">
                                    {isReadOnly
                                        ? t("page.gl_account_management.drawer.submit.view_only", { defaultValue: "View Only" })
                                        : submitting
                                        ? t("page.gl_account_management.drawer.submit.saving", { defaultValue: "Saving..." })
                                        : glAccountToEdit
                                            ? t("page.gl_account_management.drawer.submit.update", { defaultValue: "Update" })
                                            : t("page.gl_account_management.drawer.submit.create", { defaultValue: "Create" })}
                                </Button>
                            </Actions>
                        </Box>
                    </Stack>
                </Box>
            </form>
        </Drawer>
    );
};

export default CreateUpdateGLAccountDrawer;
